// Scouting panel — surfaces every alive opponent in the lobby so the
// player can see what kind of crews are still in the running before
// committing to a board shape.
//
// We don't pre-roll the next pairing here (the round's matchups happen
// at endTurn under Math.random — see pairing.ts), so the panel is a
// roster view, not a predictive "you fight X" view. That's the honest
// trade: forward-looking lobby visibility without the false-precision
// of claiming a specific matchup that the engine hasn't actually
// committed to yet.
//
// API:
//   <ScoutingPanel open={...} onClose={...} opponents={...} roundNumber={...} />

import { motion, AnimatePresence } from 'framer-motion';
import type { AIPlayer } from '../game/lobby';
import { EDGES } from '../theme/tokens';

interface ScoutingPanelProps {
  open: boolean;
  onClose: () => void;
  opponents: AIPlayer[];
  roundNumber: number;
}

export function ScoutingPanel({ open, onClose, opponents, roundNumber }: ScoutingPanelProps) {
  const alive = opponents.filter((o) => o.alive);
  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-asphalt/70 backdrop-blur-sm"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            key="scouting-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="pt-safe pb-safe flex h-full w-full max-w-md flex-col gap-md border-l bg-asphalt p-md md:p-lg"
            style={{ borderColor: EDGES.mid }}
            role="dialog"
            aria-modal="true"
            aria-label="Scout lobby"
          >
            <div className="flex items-baseline justify-between">
              <div className="flex flex-col">
                <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
                  scout · round {roundNumber}
                </p>
                <h2 className="font-cholo text-[22px] leading-none text-bone md:text-[28px]">
                  {alive.length} crew{alive.length === 1 ? '' : 's'} alive
                </h2>
              </div>
              <button
                onClick={onClose}
                aria-label="close scouting"
                className="font-data text-[11px] uppercase tracking-[0.2em] text-graphite hover:text-marker md:text-[12px]"
              >
                close ×
              </button>
            </div>

            <p className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
              your next fight is rolled when you end the turn — this is the lobby roster, not a prediction.
            </p>

            <div className="flex flex-1 flex-col gap-xs overflow-y-auto pr-xs md:gap-sm">
              {alive.map((o) => (
                <OpponentRow key={o.id} opp={o} />
              ))}
              {alive.length === 0 && (
                <p className="mt-md text-center font-data text-[12px] uppercase tracking-[0.2em] text-graphite">
                  no opponents left — last crew standing.
                </p>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function OpponentRow({ opp }: { opp: AIPlayer }) {
  const factionTone = opp.hero.faction === 'cholo' ? 'text-cholo' : 'text-emo';
  const boardCards = opp.board.filter((c): c is NonNullable<typeof c> => !!c);
  const totalAtk = boardCards.reduce((sum, c) => sum + c.attack, 0);
  const totalHp = boardCards.reduce((sum, c) => sum + c.health, 0);
  return (
    <div
      className="flex items-center justify-between gap-sm rounded-sm border bg-stucco1 px-sm py-xs md:px-md md:py-sm"
      style={{ borderColor: EDGES.soft }}
    >
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className={`truncate text-[14px] ${factionTone} ${opp.hero.faction === 'cholo' ? 'font-cholo' : 'font-emo'}`}>
          {opp.hero.name}
        </span>
        <span className="font-data text-[9px] uppercase tracking-[0.2em] text-graphite md:text-[10px]">
          HP {opp.health} · Tier {opp.tavernTier} · {boardCards.length} unit
          {boardCards.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="shrink-0 text-right font-data text-[11px] tabular-nums md:text-[12px]">
        <span className="text-aerosolRed">{totalAtk}</span>
        <span className="text-graphite"> / </span>
        <span className="text-glowStick">{totalHp}</span>
      </div>
    </div>
  );
}
